/** @jsx isolateComponent */
import { Fragment } from 'react';
import { css } from '@emotion/core';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { Animation, Colors, Shadows, Typography } from '../../../theme';
import type { ColorName } from '../../../theme';
import { Heading } from '../../display/Heading';
import { Icon } from '../../display/Icon';
import type { IconName } from '../../display/Icon';

export type AlertType = 'info' | 'success' | 'warning' | 'error';

export type Props = {
  /**
   * Controls the `aria-live` attribute of `Alert`.
   */
  ariaLive?: 'off' | 'polite' | 'assertive';
  /**
   * Controls the `role` attribute of `Alert`.
   */
  role?: 'alert' | 'alertdialog';
  /**
   * Controls the styling of `Alert` to indicate to users what type
   * of alert it is.
   */
  type?: AlertType;
  /**
   * Whether or not `Alert` has a drop shadow.
   */
  hasShadow?: boolean;
  /**
   * Controls how content within `Alert` is laid out.
   */
  layout?: 'box' | 'banner' | 'modal';

  /**
   * The title of the `Alert`.
   */
  title?: React.ReactNode;
  /**
   * The content to render within `Alert`. Typically comprised of
   * subcomponents like `AlertDescription` and `ButtonGroup`.
   */
  children?: React.ReactNode;
  /**
   * Optional function to call when `Alert` is dismissed.
   * Its presence determines whether the close button renders.
   */
  onDismiss?: (e: React.MouseEvent) => void;
};

type Configs = {
  [key in AlertType]: {
    backgroundColor: ColorName;
    color: ColorName;
    icon: IconName;
  };
};

const typeConfigs: Configs = {
  info: {
    backgroundColor: 'blue500',
    color: 'white',
    icon: 'alertInfo',
  },
  success: {
    backgroundColor: 'green400',
    color: 'grey900',
    icon: 'formCheckCircle',
  },
  warning: {
    backgroundColor: 'yellow500',
    color: 'grey900',
    icon: 'alertCircle',
  },
  error: {
    backgroundColor: 'red600',
    color: 'white',
    icon: 'alertTriangle',
  },
};

const Styles = labelStyles('Alert', {
  root: (props: Props) => [
    props.layout === 'box' && [
      css`
        label: -box;
        position: relative;
        display: grid;
        grid-template-columns: max-content 1fr max-content;
        align-items: start;
        gap: 10px;
        padding: 10px 10px 10px 20px;
        border: 1px solid ${Colors.grey200};
        border-radius: 10px;
        background-color: ${Colors.white};
        ${Typography.bodyText800};
        color: ${Colors.grey900};

        // Colored stripe that renders on the left of the box.
        &::before {
          content: '';
          display: block;
          position: absolute;
          top: 0;
          left: 0;
          bottom: 0;
          border-top-left-radius: inherit;
          border-bottom-left-radius: inherit;
          width: 10px;
        }
      `,
      css`
        label: -${props.type};
        &::before {
          background-color: ${Colors[typeConfigs[props.type!].backgroundColor]};
        }
      `,
    ],
    props.layout === 'banner' && [
      css`
        label: -banner;
        position: relative;
        display: grid;
        align-items: center;
        ${Typography.altText900};
        padding: 10px 20px;
        text-align: center;
      `,
      css`
        label: -${props.type};
        color: ${Colors[typeConfigs[props.type!].color]};
        background-color: ${Colors[typeConfigs[props.type!].backgroundColor]};

        // Rare tag selection to ensure that links are accessible
        // and consumers don't need to worry about it.
        a[class] {
          color: inherit;
          text-decoration: underline;
        }
      `,
    ],
    props.layout === 'modal' && [
      css`
        label: -modal;
        position: relative;
        max-width: 500px;
        display: flex;
        flex-direction: column;
        align-items: center;
        background-color: ${Colors.white};
        padding: 15px;
        border: 1px solid ${Colors.grey200};
        text-align: center;
        border-radius: 10px;
      `,
    ],
    props.hasShadow &&
      css`
        label: -shadowed;
        box-shadow: ${Shadows.elevation5};
      `,
  ],
  typeIcon: (props: Props) => [
    props.layout === 'modal' &&
      css`
        margin-bottom: 8px;
      `,
    props.layout === 'banner' &&
      css`
        display: inline-block;
        vertical-align: middle;
        margin-right: 5px;
        position: relative;
        top: -2px;
      `,
  ],
  titleInner: css`
    display: block;
    font-weight: 700;
  `,
  closeButton: (props: Props) => [
    props.layout !== 'banner' &&
      css`
        color: ${Colors.grey700};
        transition: ${Animation.transitionValue};

        &:hover,
        &:focus {
          color: ${Colors.grey900};
        }
      `,
    props.layout === 'banner' &&
      css`
        position: absolute;
        right: 15px;
        top: 50%;
        transform: translateY(-50%);
        color: inherit;
      `,
    props.layout === 'modal' &&
      css`
        position: absolute;
        top: 15px;
        right: 15px;
      `,
  ],
});

const defaultProps = {
  hasShadow: true,
  type: 'info',
  layout: 'box',
};

/**
 * `Alert` renders information that is immediately or near-immediately
 * important for users to know.
 */
export const Alert = (_props: Props) => {
  const props = {
    ...defaultProps,
    ..._props,
  };
  const config = typeConfigs[props.type as AlertType];
  const isBanner = props.layout === 'banner';
  const icon = (
    <div css={Styles.typeIcon(props)}>
      <Icon
        ariaLabel={props.type}
        color={isBanner ? 'currentColor' : config.backgroundColor}
        isBlock={true}
        name={config.icon}
        size={24}
      />
    </div>
  );
  const closeButton = props.onDismiss && (
    <button
      type="button"
      aria-label="Dismiss"
      css={Styles.closeButton(props)}
      onClick={props.onDismiss}
    >
      <Icon isBlock={true} name="formX" size={24} />
    </button>
  );
  // Use `&nbsp;` to prevent `icon` from wrapping and becoming a widow/orphan.
  const bannerIcon = isBanner && icon && <Fragment>{icon}&nbsp;</Fragment>;
  return (
    <article
      aria-live={props.ariaLive}
      css={Styles.root(props)}
      role={props.role}
    >
      {!isBanner && icon}
      <div css={Styles.content}>
        {props.title && (
          <Heading css={Styles.title}>
            <span css={Styles.titleInner}>
              {bannerIcon}
              {props.title}
            </span>
          </Heading>
        )}
        {props.children}
      </div>
      {closeButton}
    </article>
  );
};
